import { useEffect, useCallback, memo } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "store/hooks";
import { useI18n } from "i18n";
import EventTrackingProvider from "modules/eventTracking/context/eventTrackingContext";
import { EventStatusSubscriber } from "modules/eventStatus";
import { setCurrentTheater } from "modules/theater/theaterSlice";
import { selectUser } from "modules/auth/redux/selectors";
import TheaterViewContextProvider from "modules/theater/TheaterContext";
import TheaterView from "modules/theater/TheaterView";
import { EndEventNotification } from "modules/event/components";
import { useUserSession } from "modules/eventUserPresence/hooks/userSession";
import { UserForceMoveSubscriber } from "modules/userForceMove/components/UserForceMoveSubscriber";
import { usePageSettingReducerActions } from "modules/pageSetting/context/pageSettingReducer";
import UsersInEventSubscriber from "../usersInEvent/UsersInEventSubscriber";
import { selectCurrentEventName, selectTheater } from "../selectors";
import useEnterLeaveEvent from "../hooks/useEnterLeaveEvent";
import EventViewDataLayer from "./EventViewDataLayer";
import EventPreChecks from "./EventPreChecks";
import { IEvent } from "../types";
import { useIsInEvent } from "../hooks";

interface Props {
  event: IEvent;
}

const EventViewContainer = memo<Props>((props) => {
  const { event: currentEvent } = props;
  const dispatch = useAppDispatch();
  const eventId = currentEvent.id;
  const { setPageTitle } = usePageSettingReducerActions();
  const user = useSelector(selectUser);
  const isInEvent = useIsInEvent();
  const currentEventName = useSelector(selectCurrentEventName);
  const currentEventTheater = useSelector(selectTheater);
  const { enterEvent, leaveEvent } = useEnterLeaveEvent(eventId);
  const { t } = useI18n(["common", "event"]);

  const { clearCurrentSession } = useUserSession();

  useEffect(() => {
    if (currentEventName) {
      const eventName = currentEventName || "";

      setPageTitle(eventName);

      // Update only if theater object is passed, if it is just theater id ignore it
      // To avoid white page without any error
      if (isInEvent && currentEventTheater) {
        dispatch(setCurrentTheater(currentEventTheater));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInEvent, currentEventTheater]);

  const onPageClose = useCallback(() => {
    if (user) {
      clearCurrentSession({ eventId });
    }
    leaveEvent();
  }, [user, eventId, clearCurrentSession, leaveEvent]);

  useEffect(() => {
    if (!user) return;

    enterEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    window.addEventListener("pagehide", onPageClose);

    return () => {
      leaveEvent();

      if (user) {
        clearCurrentSession({ eventId });
      }

      window.removeEventListener("pagehide", onPageClose);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  return (
    <EventPreChecks data-testid="event-pre-check">
      <EventTrackingProvider>
        <EventViewDataLayer>
          <TheaterViewContextProvider>
            <TheaterView data-testid="theater-view" />
          </TheaterViewContextProvider>
          {isInEvent && <EndEventNotification />}
          {isInEvent && <EventStatusSubscriber />}
          {user && isInEvent && <UsersInEventSubscriber />}
          <UserForceMoveSubscriber />
        </EventViewDataLayer>
      </EventTrackingProvider>
    </EventPreChecks>
  );
});

export default EventViewContainer;
